import { Capacitor, registerPlugin } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'

/** Android-only: nav bar color + navigationBars().bottom (see NavigationBarPlugin.java). */
const AppNavigationBar = registerPlugin<{
  setBackgroundColor(options: { color: string; lightNavigationBar?: boolean }): Promise<void>
  getInsets(): Promise<{ bottom: number; navigationBar?: number }>
}>('AppNavigationBar')

/** Same key as ThemeProvider in `ThemeContext.tsx` — keep in sync. */
const THEME_STORAGE_KEY = 'getmoretats-theme-mode'

const THEME_BACKGROUND = {
  dark: '#262626',
  light: '#ffffff',
} as const

function readStoredTheme(): 'light' | 'dark' {
  try {
    if (localStorage.getItem(THEME_STORAGE_KEY) === 'light') return 'light'
  } catch {
    /* ignore */
  }
  return 'dark'
}

const ANDROID_STATUS_TOP_FALLBACK_PX = 24
const ANDROID_NAV_BOTTOM_FALLBACK_PX = 48
/** Pill sits above system buttons — clamped so OEM inset spikes do not shove it off-screen. */
const ANDROID_PILL_BOTTOM_MIN_PX = 32
const ANDROID_PILL_BOTTOM_MAX_PX = 42
const ANDROID_PILL_BOTTOM_DEFAULT_PX = 36

export function androidBottomNavPillPx(nativeInsetPx: number): number {
  const inset = nativeInsetPx > 0 ? nativeInsetPx : ANDROID_NAV_BOTTOM_FALLBACK_PX
  const raw = Math.round(inset * 0.75)
  return Math.max(
    ANDROID_PILL_BOTTOM_MIN_PX,
    Math.min(ANDROID_PILL_BOTTOM_MAX_PX, raw || ANDROID_PILL_BOTTOM_DEFAULT_PX),
  )
}

function readEnvSafeAreaBottomPx(): number {
  if (typeof document === 'undefined') return 0

  const probe = document.createElement('div')
  probe.style.cssText =
    'position:fixed;left:-9999px;visibility:hidden;padding-bottom:env(safe-area-inset-bottom);'
  document.documentElement.appendChild(probe)
  const px = parseFloat(getComputedStyle(probe).paddingBottom) || 0
  document.documentElement.removeChild(probe)
  return px
}

async function applyIosPalette(isDark: boolean): Promise<void> {
  const color = isDark ? THEME_BACKGROUND.dark : THEME_BACKGROUND.light

  await StatusBar.setBackgroundColor({ color })
  await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
}

/** Android WebView often reports env(safe-area-inset-top) as 0 — inject native height for CSS. */
async function syncAndroidStatusTopInset(): Promise<void> {
  try {
    const statusBarInfo = await StatusBar.getInfo()
    const statusBarHeightPx =
      'height' in statusBarInfo && typeof statusBarInfo.height === 'number'
        ? statusBarInfo.height
        : 0
    const topPx = statusBarHeightPx > 0 ? statusBarHeightPx : ANDROID_STATUS_TOP_FALLBACK_PX
    document.documentElement.style.setProperty('--gmt-android-min-status-top', `${topPx}px`)
  } catch {
    document.documentElement.style.setProperty(
      '--gmt-android-min-status-top',
      `${ANDROID_STATUS_TOP_FALLBACK_PX}px`,
    )
  }
}

/** Read classic 3-button / gesture nav strip height from native WindowInsets. */
export async function readAndroidNavigationBarInsetPx(): Promise<number> {
  let nativeBottom = 0

  try {
    const insets = await AppNavigationBar.getInsets()
    nativeBottom = Math.max(insets.navigationBar ?? insets.bottom ?? 0, 0)
  } catch {
    /* Plugin missing until native rebuild. */
  }

  const envBottom = readEnvSafeAreaBottomPx()

  return nativeBottom > 0
    ? nativeBottom
    : envBottom > 0
      ? envBottom
      : ANDROID_NAV_BOTTOM_FALLBACK_PX
}

/** Pin CSS vars used by BottomNavWrapper + page pb-bottom-nav offset. */
export async function syncAndroidBottomNavInset(): Promise<void> {
  const insetPx = await readAndroidNavigationBarInsetPx()
  const pillPx = androidBottomNavPillPx(insetPx)

  document.documentElement.style.setProperty('--gmt-android-nav-bottom-inset', `${insetPx}px`)
  document.documentElement.style.setProperty('--bottom-nav-pill-bottom', `${pillPx}px`)
  document.documentElement.style.setProperty('--bottom-nav-lift', `${pillPx}px`)
  document.documentElement.style.setProperty('--bottom-nav-bottom-gutter', '0px')
}

/**
 * Status bar overlay for frosted headers. Nav bar color matches app theme.
 * Bottom nav: BottomNavWrapper bottom = navigationBars().bottom (via useAndroidBottomNavOffset).
 */
async function applyAndroidWindowChrome(isDark: boolean): Promise<void> {
  await StatusBar.setOverlaysWebView({ overlay: true })
  await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
  await syncAndroidStatusTopInset()
  await syncAndroidBottomNavInset()

  try {
    await AppNavigationBar.setBackgroundColor({
      color: '#00000000',
      lightNavigationBar: !isDark,
    })
  } catch {
    console.debug('AppNavigationBar color update skipped')
  }
}

/**
 * Native status bar: separate strategies per platform.
 *
 * Android: BottomNavWrapper lifted by navigationBars().bottom from NavigationBarPlugin.
 * Top layout uses --gmt-android-min-status-top for overlay headers.
 *
 * iOS: layout uses env(safe-area-inset-*) under html.cap-ios-native.
 */
export async function syncNativeStatusBarForTheme(theme: 'light' | 'dark'): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  const isDark = theme === 'dark'

  try {
    if (Capacitor.getPlatform() === 'android') {
      await applyAndroidWindowChrome(isDark)
      return
    }

    await applyIosPalette(isDark)
  } catch {
    console.debug('Native StatusBar update skipped (not available in this context)')
  }
}

function scheduleAndroidInsetResync(): void {
  const resync = () => {
    void syncAndroidStatusTopInset()
    void syncAndroidBottomNavInset()
  }

  window.setTimeout(resync, 250)
  window.setTimeout(resync, 900)
}

/** Earliest sensible moment before React mounts; reduces time WebView paints under the Android status bar. */
export function bootstrapNativeStatusBarAppearance(): void {
  if (!Capacitor.isNativePlatform()) return

  if (Capacitor.getPlatform() === 'android') {
    void syncAndroidBottomNavInset()
  }

  void syncNativeStatusBarForTheme(readStoredTheme())

  if (Capacitor.getPlatform() === 'android') {
    scheduleAndroidInsetResync()

    const resyncAndroidInsets = () => {
      void syncAndroidStatusTopInset()
      void syncAndroidBottomNavInset()
    }

    window.addEventListener('orientationchange', () => {
      window.setTimeout(resyncAndroidInsets, 200)
    })

    window.visualViewport?.addEventListener('resize', resyncAndroidInsets)
  }
}
